/**
 * Composant ImageSelector
 * Gestion de la sélection d'images (IA, Pexels, upload personnalisé)
 */
import React from 'react';
import { Upload, Loader2, Trash2 } from 'lucide-react';

const STATIC_URL = process.env.REACT_APP_BACKEND_URL || '';

export const ImageSelector = ({
  siteId,
  articleData,
  isExtra,
  sites,
  uploadingCustomImage,
  onUpdateArticle,
  onCustomImageUpload
}) => {
  const hasAi = !!articleData.ai_image_url;
  const hasPeriscope = articleData.periscope_images?.length > 0;
  const hasFeatured = !!articleData.featured_image_url && !hasAi && !hasPeriscope;

  // Construire la liste de toutes les images disponibles
  const allImages = [];
  if (hasAi) {
    allImages.push({
      url: articleData.ai_image_url,
      displayUrl: articleData.ai_image_url.startsWith('http') ? articleData.ai_image_url : `${STATIC_URL}${articleData.ai_image_url}`,
      label: 'Image IA',
      source: 'ia',
      sourceColor: 'bg-cyan-900/40 text-cyan-400',
    });
  }
  if (hasPeriscope) {
    articleData.periscope_images.forEach((img, idx) => {
      allImages.push({
        url: img.full_url || img.preview_url,
        displayUrl: img.preview_url || img.full_url,
        label: img.title?.substring(0, 30) || `Image ${idx + 1}`,
        source: img.source,
        sourceColor: 'bg-purple-900/40 text-purple-400',
        periscopeImg: img,
      });
    });
  }

  const handleSelectImage = (img) => {
    onUpdateArticle(siteId, {
      featured_image_url: img.url,
      selected_periscope_image: img.periscopeImg || null,
      no_image: false
    });
  };

  const handleNoImage = () => {
    onUpdateArticle(siteId, {
      featured_image_url: null,
      selected_periscope_image: null,
      no_image: true
    });
  };

  const handleReviewImages = () => {
    onUpdateArticle(siteId, {
      featured_image_url: allImages[0]?.url,
      no_image: false
    });
  };

  // Cas 1 : Plusieurs images à choisir
  if (allImages.length > 1 && !articleData.no_image) {
    const cols = allImages.length <= 3 ? 'grid-cols-3' : 'grid-cols-4';
    return (
      <div>
        <p className="text-xs text-amber-400 uppercase mb-2 font-semibold">
          Choisissez l&apos;image ({allImages.length} résultats)
        </p>
        <div className={`grid ${cols} gap-2`} data-testid="image-choices">
          {allImages.map((img, idx) => {
            const isSelected = articleData.featured_image_url === img.url;
            return (
              <div 
                key={img.url || `img-${idx}`}
                onClick={() => handleSelectImage(img)}
                className={`cursor-pointer rounded-lg overflow-hidden border-2 transition-all hover:scale-[1.02] ${
                  isSelected
                    ? 'border-purple-500 ring-2 ring-purple-500/30 shadow-lg shadow-purple-500/10'
                    : 'border-slate-700 hover:border-slate-500'
                }`}
                data-testid={`img-choice-${idx}`}
              >
                <div className="relative">
                  <img src={img.displayUrl} alt={img.label} className="w-full h-28 object-cover" onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"/>' }} />
                  {isSelected && (
                    <div className="absolute top-1 right-1 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                    </div>
                  )}
                </div>
                <div className="px-1.5 py-1 bg-slate-900/80 flex items-center justify-between">
                  <span className="text-[10px] text-slate-400 truncate">{img.label}</span>
                  <span className={`text-[9px] font-mono px-1 py-0.5 rounded ${img.sourceColor}`}>{img.source}</span>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-2 mt-2">
          {!articleData.featured_image_url && (
            <p className="text-xs text-slate-600 italic">Cliquez sur une image pour la sélectionner</p>
          )}
          {articleData.featured_image_url && (
            <button
              onClick={handleNoImage}
              className="text-xs text-red-400/70 hover:text-red-400 transition-colors underline underline-offset-2"
              data-testid={`no-image-btn-${siteId}`}
            >
              Aucune image
            </button>
          )}
        </div>
        
        {/* Upload personnalisé */}
        {!isExtra && <CustomImageUpload 
          siteId={siteId}
          articleData={articleData}
          sites={sites}
          uploadingCustomImage={uploadingCustomImage}
          onUpdateArticle={onUpdateArticle}
          onCustomImageUpload={onCustomImageUpload}
        />}
      </div>
    );
  }

  // Cas 1b : Images désélectionnées volontairement
  if (allImages.length > 0 && articleData.no_image) {
    return (
      <div>
        <div className="flex items-center gap-3 p-3 rounded-lg border border-dashed border-slate-700 bg-slate-900/30">
          <p className="text-xs text-slate-500 italic flex-1">Aucune image sélectionnée</p>
          <button
            onClick={handleReviewImages}
            className="text-xs text-cyan-400/70 hover:text-cyan-400 transition-colors underline underline-offset-2"
          >
            Revoir les images
          </button>
        </div>
        {!isExtra && <CustomImageUpload 
          siteId={siteId}
          articleData={articleData}
          sites={sites}
          uploadingCustomImage={uploadingCustomImage}
          onUpdateArticle={onUpdateArticle}
          onCustomImageUpload={onCustomImageUpload}
        />}
      </div>
    );
  }

  // Cas 2 : Une seule image (IA seule ou featured)
  if (allImages.length === 1 || hasFeatured) {
    const imgSrc = allImages.length === 1 
      ? allImages[0].displayUrl 
      : (articleData.featured_image_url?.startsWith('http') ? articleData.featured_image_url : `${STATIC_URL}${articleData.featured_image_url}`);
    return (
      <div>
        <div className="rounded-lg overflow-hidden border border-slate-700">
          <img src={imgSrc} alt="Image article" className="w-full h-48 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
        <button
          onClick={handleNoImage}
          className="mt-1.5 text-xs text-red-400/70 hover:text-red-400 transition-colors underline underline-offset-2"
          data-testid={`no-image-single-btn-${siteId}`}
        >
          Aucune image
        </button>
        {!isExtra && <CustomImageUpload 
          siteId={siteId}
          articleData={articleData}
          sites={sites}
          uploadingCustomImage={uploadingCustomImage}
          onUpdateArticle={onUpdateArticle}
          onCustomImageUpload={onCustomImageUpload}
        />}
      </div>
    );
  }

  // Cas 3 : Pas d'image disponible
  return !isExtra ? (
    <CustomImageUpload 
      siteId={siteId}
      articleData={articleData}
      sites={sites}
      uploadingCustomImage={uploadingCustomImage}
      onUpdateArticle={onUpdateArticle}
      onCustomImageUpload={onCustomImageUpload}
    />
  ) : null;
};

// Sous-composant pour l'upload d'image personnalisée
const CustomImageUpload = ({
  siteId,
  articleData,
  sites,
  uploadingCustomImage,
  onUpdateArticle,
  onCustomImageUpload
}) => {
  const site = sites.find(s => s.id === siteId);
  const isConnected = site?.is_connected !== false;
  const isConfigured = (site?.pitch?.trim().length > 0) || (site?.default_prompt?.trim().length > 0);
  const isSiteReady = isConnected && isConfigured;
  
  if (!isSiteReady) return null;
  
  const isUploading = uploadingCustomImage[siteId];
  const hasCustomImage = articleData.custom_image_uploaded;
  
  const handleRemoveCustomImage = () => {
    onUpdateArticle(siteId, {
      featured_image_url: null,
      custom_image_uploaded: false,
      custom_image_filename: null
    });
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-800/50">
      <div className="flex items-center gap-2 mb-2">
        <Upload className="w-4 h-4 text-emerald-400" />
        <p className="text-xs text-emerald-400 uppercase font-semibold">
          Image personnalisée
        </p>
        {hasCustomImage && (
          <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded font-mono">
            ✓ Uploadée
          </span>
        )}
      </div>
      
      {hasCustomImage ? (
        <div className="flex items-center gap-3 p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
          <div className="flex-1">
            <p className="text-sm text-emerald-300">{articleData.custom_image_filename || 'Image uploadée'}</p>
            <p className="text-[10px] text-slate-500">Sera utilisée comme image d&apos;illustration</p>
          </div>
          <button
            onClick={handleRemoveCustomImage}
            className="text-red-400/70 hover:text-red-400 transition-colors p-1"
            title="Supprimer l'image"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <label className={`flex items-center gap-3 p-3 border-2 border-dashed rounded-lg cursor-pointer transition-all ${
          isUploading 
            ? 'border-emerald-500/50 bg-emerald-500/5' 
            : 'border-slate-700 hover:border-emerald-500/50 hover:bg-slate-900/50'
        }`}>
          {isUploading ? (
            <>
              <Loader2 className="w-5 h-5 text-emerald-400 animate-spin" />
              <span className="text-sm text-emerald-300">Upload en cours...</span>
            </>
          ) : (
            <>
              <Upload className="w-5 h-5 text-slate-500" />
              <div>
                <p className="text-sm text-slate-400">Uploader une image</p>
                <p className="text-[10px] text-slate-600">JPG, PNG, WebP • Max 10 Mo</p>
              </div>
            </>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => onCustomImageUpload(e, siteId, articleData)}
            className="hidden"
            disabled={isUploading}
            data-testid={`custom-image-upload-${siteId}`}
          />
        </label>
      )}
      <p className="text-[10px] text-slate-600 mt-1.5">
        Le nom du fichier sera automatiquement basé sur le slug de l&apos;article. 
        Le titre sera utilisé pour la légende et le texte alternatif.
      </p>
    </div>
  );
};

export default ImageSelector;
